import { useState, type ReactNode } from "react";
import type {
  BreakerView,
  CredentialMetadataView,
  FailureStateName,
  GameStatusView,
  IntegrationHealth,
  IntegrationStatus,
  IntegrationTypeName,
  IncidentGroupView,
  InboxItemType,
  InboxItemView,
  RssItemView,
  SafeIntegration,
  SafeTestResult,
  StreamEventView,
  WorkflowExecutionView,
  WorkflowFailureView,
  WorkshopUpdateView,
} from "../lib/api";
import type { StatusTone } from "./ui";

/**
 * Phase 7 presentational layer (sections 26-31, 34). Every component here is a
 * pure projection of the SAFE data the control API returns.
 *
 * SECRET-SAFETY CONTRACT (enforced by construction, not just convention):
 *  - There is NO prop, field or code path in this file that renders a signing
 *    secret, a credential value, an n8n token or a callback token. Credentials
 *    surface ONLY as metadata (kind / key version / present).
 *  - `test` is always the SAFE validate-and-preview action, rendered as a clearly
 *    labelled, explicit button — never wired to a live send.
 *  - The game-server + workshop surfaces expose NO control that could restart a
 *    production server or apply a mod/workshop update. Only monitoring +
 *    maintenance acknowledgement exist.
 */

// ── Safety copy (reused verbatim so tests can assert it) ──────────────────────
export const NO_SECRETS_NOTE =
  "Secret values are never shown here. Credentials appear only as status — configured or not — never the value itself.";
export const SAFE_TEST_NOTE =
  "This runs a SAFE test: your configuration is validated and previewed only. Nothing is sent, activated, or written to Discord.";
export const SEND_TEST_LABEL = "Run safe test (validate & preview only)";
export const NO_AUTO_RESTART_NOTE =
  "ODIN monitors this server and never restarts it. No mod or workshop update is ever applied automatically — ODIN only detects and reports updates.";
export const PROVIDER_UNVALIDATED_NOTE =
  "Not connected. A streaming provider is shown as connected only after its identifier passes validation.";

// ── Labels + tone maps ────────────────────────────────────────────────────────
export const INTEGRATION_TYPE_LABELS: Record<IntegrationTypeName, string> = {
  N8N: "n8n automation",
  RSS: "RSS feed",
  TWITCH: "Twitch stream",
  YOUTUBE: "YouTube channel",
  GAME_SERVER: "Game server",
  STEAM_WORKSHOP: "Steam Workshop",
  WEBHOOK_OUTBOUND: "Outbound webhook",
  EMAIL_NOTIFICATION: "Email notification",
  BACKUP_MONITOR: "Backup monitor",
  DEPLOYMENT_NOTIFICATION: "Deployment notification",
};

/** The credential kind ODIN's store expects for each type (metadata only). */
export const CREDENTIAL_KIND_BY_TYPE: Partial<Record<IntegrationTypeName, string>> = {
  TWITCH: "twitch-client-secret",
  YOUTUBE: "youtube-api-key",
  WEBHOOK_OUTBOUND: "webhook-outbound-secret",
  EMAIL_NOTIFICATION: "smtp-password",
  RSS: "rss-basic-auth",
  N8N: "n8n-service-credential",
  GAME_SERVER: "game-server-api-key",
};

export function statusTone(status: IntegrationStatus): StatusTone {
  switch (status) {
    case "HEALTHY":
      return "ok";
    case "DEGRADED":
    case "RATE_LIMITED":
    case "VALIDATING":
    case "CONFIGURING":
      return "warn";
    case "FAILED":
    case "REVOKED":
      return "danger";
    default:
      return "neutral";
  }
}

export function failureStateTone(state: FailureStateName): StatusTone {
  switch (state) {
    case "RESOLVED":
      return "ok";
    case "DEAD_LETTER":
    case "MANUAL_REVIEW":
      return "danger";
    case "CANCELLED":
      return "neutral";
    default:
      return "warn";
  }
}

export function inboxTone(type: InboxItemType): StatusTone {
  switch (type) {
    case "SUCCESS":
      return "ok";
    case "WARNING":
    case "ACTION_REQUIRED":
      return "warn";
    case "ERROR":
      return "danger";
    default:
      return "neutral";
  }
}

// ── Tiny primitives ───────────────────────────────────────────────────────────
export function StatusPill({ tone, label }: { tone: StatusTone; label: string }): JSX.Element {
  return (
    <span className={`status status--${tone}`} role="status">
      <span className="status__dot" aria-hidden="true" />
      {label}
    </span>
  );
}

export function IntegrationStatusBadge({ status }: { status: IntegrationStatus }): JSX.Element {
  return <StatusPill tone={statusTone(status)} label={status} />;
}

function fmt(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toUTCString();
}

export function KeyValues({ rows }: { rows: [string, ReactNode][] }): JSX.Element {
  return (
    <dl className="kv-grid">
      {rows.map(([k, v], i) => (
        <div className="kv" key={`${k}-${i}`}>
          <dt className="kv__k">{k}</dt>
          <dd className="kv__v">{v}</dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * Credential-health chip. Shows STATE ONLY — configured / not configured / not
 * required — plus non-secret metadata (kind + key version + rotation count). It
 * is structurally impossible for this chip to render a secret: it never receives
 * one (the API only ever returns metadata).
 */
export function CredentialHealthChip({
  requiresCredential,
  hasCredential,
  credentials,
}: {
  requiresCredential: boolean;
  hasCredential: boolean;
  credentials?: CredentialMetadataView[];
}): JSX.Element {
  if (!requiresCredential && !hasCredential) {
    return <span className="count-chip count-chip--neutral">No external credential required</span>;
  }
  const present = credentials?.find((c) => c.present) ?? (hasCredential ? undefined : null);
  if (hasCredential || present) {
    const meta = present ?? undefined;
    return (
      <span className="count-chip count-chip--ok" title="ODIN never displays the value">
        Credential configured
        {meta ? ` · ${meta.kind} · key ${meta.keyVersion} · rev ${meta.version}` : ""}
      </span>
    );
  }
  return <span className="count-chip count-chip--warn">Credential required — not configured</span>;
}

export function BreakerChip({ breaker }: { breaker: BreakerView }): JSX.Element {
  const tone: StatusTone = breaker.state === "CLOSED" ? "ok" : breaker.state === "HALF_OPEN" ? "warn" : "danger";
  return (
    <span className={`count-chip count-chip--${tone === "danger" ? "danger" : tone === "warn" ? "warn" : "ok"}`}>
      {breaker.scope}: {breaker.state} ({breaker.consecutiveFailures} fail)
    </span>
  );
}

// ── Safe test panel (explicit, clearly-labelled action) ───────────────────────
export function SafeTestPanel({
  result,
  onRunTest,
  busy,
}: {
  result: SafeTestResult | null;
  onRunTest: () => void;
  busy?: boolean;
}): JSX.Element {
  return (
    <div className="world-panel world-panel--proposed" style={{ marginTop: "0.75rem" }}>
      <h4 className="section-title" style={{ marginTop: 0 }}>
        Safe test
      </h4>
      <p className="muted">{SAFE_TEST_NOTE}</p>
      <button type="button" className="btn btn--secondary" onClick={onRunTest} disabled={busy}>
        {busy ? "Running…" : SEND_TEST_LABEL}
      </button>
      {result ? (
        <div className="ob-findings" style={{ marginTop: "0.5rem" }} aria-label="Safe test result">
          <StatusPill tone={result.ok ? "ok" : "warn"} label={result.ok ? "Preview OK" : "Preview found issues"} />
          <p className="muted" style={{ marginTop: "0.5rem" }}>
            {result.note}
          </p>
          <ValidationFindings validation={result.validation} />
        </div>
      ) : null}
    </div>
  );
}

export function ValidationFindings({
  validation,
}: {
  validation: { valid: boolean; errors: { path: string; message: string }[] };
}): JSX.Element {
  if (validation.valid) {
    return <p className="muted">Configuration is valid.</p>;
  }
  return (
    <ul className="finding-list" aria-label="Validation findings">
      {validation.errors.map((e, i) => (
        <li className="finding-row ob-finding--danger" key={`${e.path}-${i}`}>
          <span className="finding-row__fix-label">{e.path || "config"}</span>
          <span className="finding-row__plain">{e.message}</span>
        </li>
      ))}
    </ul>
  );
}

// ── Overview: integration card ────────────────────────────────────────────────
export type IntegrationAction = "validate" | "test" | "pause" | "resume" | "revoke" | "history" | "configure";

export function IntegrationCard({
  integration,
  health,
  credentials,
  onAction,
  busy,
  testResult,
}: {
  integration: SafeIntegration;
  health?: IntegrationHealth;
  credentials?: CredentialMetadataView[];
  onAction: (action: IntegrationAction, integration: SafeIntegration) => void;
  busy?: boolean;
  testResult?: SafeTestResult | null;
}): JSX.Element {
  const canPause = integration.status === "HEALTHY" || integration.status === "DEGRADED" || integration.status === "RATE_LIMITED";
  const canResume = integration.status === "PAUSED";
  const revoked = integration.status === "REVOKED";
  const breaker = health?.breakers?.[0];
  return (
    <section className="card" aria-label={`Integration ${integration.name}`}>
      <div className="row" style={{ justifyContent: "space-between", alignItems: "center" }}>
        <h3 className="card__title" style={{ margin: 0 }}>
          {integration.name}
        </h3>
        <IntegrationStatusBadge status={integration.status} />
      </div>
      <p className="card__desc">{INTEGRATION_TYPE_LABELS[integration.type]}</p>

      <KeyValues
        rows={[
          ["Type", integration.type],
          ["Last success", fmt(integration.lastHealthyAt)],
          ["Last validation", fmt(integration.lastValidationAt)],
          ["Last error", integration.safeErrorSummary ?? integration.lastErrorCode ?? "None"],
          ["Notification destination", integration.guildId ? `Guild ${integration.guildId}` : "Tenant dashboard"],
          [
            "Credential health",
            <CredentialHealthChip
              key="cred"
              requiresCredential={integration.requiresCredential}
              hasCredential={integration.hasCredential}
              credentials={credentials}
            />,
          ],
          [
            "Callback / circuit-breaker",
            breaker ? <BreakerChip key="brk" breaker={breaker} /> : <span className="count-chip count-chip--neutral">No breaker tripped</span>,
          ],
        ]}
      />

      {health && health.findings.length > 0 ? (
        <ul className="finding-list" aria-label="Validation findings">
          {health.findings.map((f, i) => (
            <li className={`finding-row ${f.resolved ? "ob-finding--neutral" : "ob-finding--warn"}`} key={`${f.code}-${i}`}>
              <span className="finding-row__fix-label">
                {f.severity} · {f.code}
              </span>
              <span className="finding-row__plain">{f.summary}</span>
            </li>
          ))}
        </ul>
      ) : null}

      <div className="row" style={{ gap: "0.5rem", flexWrap: "wrap", marginTop: "0.5rem" }}>
        <button type="button" className="btn btn--ghost" disabled={busy || revoked} onClick={() => onAction("configure", integration)}>
          Configure
        </button>
        <button type="button" className="btn btn--ghost" disabled={busy || revoked} onClick={() => onAction("validate", integration)}>
          Validate
        </button>
        <button type="button" className="btn btn--secondary" disabled={busy || revoked} onClick={() => onAction("test", integration)}>
          {SEND_TEST_LABEL}
        </button>
        {canPause ? (
          <button type="button" className="btn btn--ghost" disabled={busy} onClick={() => onAction("pause", integration)}>
            Pause
          </button>
        ) : null}
        {canResume ? (
          <button type="button" className="btn btn--ghost" disabled={busy} onClick={() => onAction("resume", integration)}>
            Resume
          </button>
        ) : null}
        <button type="button" className="btn btn--ghost" disabled={busy} onClick={() => onAction("history", integration)}>
          Execution history
        </button>
        <button type="button" className="btn btn--danger-confirm" disabled={busy || revoked} onClick={() => onAction("revoke", integration)}>
          Revoke
        </button>
      </div>

      {testResult ? (
        <div className="ob-findings" style={{ marginTop: "0.5rem" }} aria-label="Safe test result">
          <StatusPill tone={testResult.ok ? "ok" : "warn"} label={testResult.ok ? "Preview OK" : "Preview found issues"} />
          <p className="muted">{testResult.note}</p>
        </div>
      ) : null}
    </section>
  );
}

export function IntegrationOverview({
  integrations,
  onAction,
  busyId,
  testResults,
}: {
  integrations: SafeIntegration[];
  onAction: (action: IntegrationAction, integration: SafeIntegration) => void;
  busyId?: string | null;
  testResults?: Record<string, SafeTestResult | null>;
}): JSX.Element {
  if (integrations.length === 0) {
    return (
      <div className="state">
        <p className="state__title">No integrations yet</p>
        <p>Add an integration to start monitoring RSS, streams, game servers, backups or n8n workflows.</p>
      </div>
    );
  }
  return (
    <div className="card-grid" aria-label="Integrations">
      {integrations.map((i) => (
        <IntegrationCard
          key={i.id}
          integration={i}
          onAction={onAction}
          busy={busyId === i.id}
          testResult={testResults?.[i.id] ?? null}
        />
      ))}
    </div>
  );
}

// ── Add integration form ──────────────────────────────────────────────────────
export function AddIntegrationForm({
  types,
  onCreate,
  busy,
}: {
  types: readonly IntegrationTypeName[];
  onCreate: (input: { type: IntegrationTypeName; name: string; guildId: string | null }) => void;
  busy?: boolean;
}): JSX.Element {
  const [type, setType] = useState<IntegrationTypeName>(types[0] ?? "RSS");
  const [name, setName] = useState("");
  const [guildId, setGuildId] = useState("");
  const canCreate = name.trim().length > 0;
  return (
    <div className="world-panel world-panel--proposed" style={{ marginBottom: "0.75rem" }}>
      <h4 className="section-title" style={{ marginTop: 0 }}>
        Add integration
      </h4>
      <div className="card-grid">
        <label className="field">
          <span className="field__label">Type</span>
          <select className="field__input" value={type} onChange={(e) => setType(e.target.value as IntegrationTypeName)} aria-label="Integration type">
            {types.map((t) => (
              <option key={t} value={t}>
                {INTEGRATION_TYPE_LABELS[t]}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span className="field__label">Name</span>
          <input className="field__input" value={name} onChange={(e) => setName(e.target.value)} aria-label="Integration name" />
        </label>
        <label className="field">
          <span className="field__label">Target guild id (optional)</span>
          <input className="field__input" value={guildId} onChange={(e) => setGuildId(e.target.value)} aria-label="Integration target guild id" />
        </label>
      </div>
      <button
        type="button"
        className="btn btn--primary"
        disabled={busy || !canCreate}
        onClick={() => onCreate({ type, name: name.trim(), guildId: guildId.trim() || null })}
        style={{ marginTop: "0.5rem" }}
      >
        {busy ? "Creating…" : "Create draft integration"}
      </button>
      <p className="muted" style={{ marginTop: "0.5rem" }}>
        A new integration is created as a draft (CONFIGURING). It is never activated automatically. {NO_SECRETS_NOTE}
      </p>
    </div>
  );
}

// ── Provider read views ───────────────────────────────────────────────────────
export function RssPreview({ items }: { items: RssItemView[] }): JSX.Element {
  if (items.length === 0) return <p className="muted">No feed items ingested yet.</p>;
  return (
    <table className="table" aria-label="Normalized feed items">
      <thead>
        <tr className="table__head">
          <th>Title</th>
          <th>Published</th>
          <th>Notified</th>
        </tr>
      </thead>
      <tbody>
        {items.map((it) => (
          <tr className="table__row" key={it.externalId}>
            <td>{it.title}</td>
            <td>{fmt(it.publishedAt)}</td>
            <td>{it.notified ? "yes" : "no"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function StreamHistory({ events }: { events: StreamEventView[] }): JSX.Element {
  if (events.length === 0) return <p className="muted">No live sessions recorded yet.</p>;
  return (
    <table className="table" aria-label="Live session history">
      <thead>
        <tr className="table__head">
          <th>Event</th>
          <th>Title</th>
          <th>Started</th>
          <th>Notified</th>
        </tr>
      </thead>
      <tbody>
        {events.map((e, i) => (
          <tr className="table__row" key={`${e.eventType}-${i}`}>
            <td>{e.eventType}</td>
            <td>{e.title ?? "—"}</td>
            <td>{fmt(e.startedAt)}</td>
            <td>{e.notified ? "yes" : "no"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function GameStatusCard({ status }: { status: GameStatusView | null }): JSX.Element {
  if (!status) return <p className="muted">No status ingested yet.</p>;
  return (
    <KeyValues
      rows={[
        ["State", <StatusPill key="s" tone={status.online ? "ok" : "danger"} label={status.online ? "Online" : "Offline"} />],
        ["Players", status.playerCount === null ? "—" : `${status.playerCount}${status.maxPlayers !== null ? ` / ${status.maxPlayers}` : ""}`],
        ["Map", status.map ?? "—"],
        ["Confidence", status.confidence],
        ["Ingested", fmt(status.ingestedAt)],
      ]}
    />
  );
}

/**
 * Workshop monitor. Shows detected updates + a maintenance-acknowledgement
 * action. It deliberately offers NO "apply update" / "update mod" control — the
 * only mutation is acknowledging that a human handled maintenance.
 */
export function WorkshopMonitor({
  updates,
  onAcknowledge,
  busy,
}: {
  updates: WorkshopUpdateView[];
  onAcknowledge?: (update: WorkshopUpdateView) => void;
  busy?: boolean;
}): JSX.Element {
  return (
    <div>
      <div className="dryrun-banner" role="note">
        <strong>Monitoring only.</strong>
        <span>{NO_AUTO_RESTART_NOTE}</span>
      </div>
      {updates.length === 0 ? (
        <p className="muted">No workshop updates detected yet.</p>
      ) : (
        <table className="table" aria-label="Detected workshop updates">
          <thead>
            <tr className="table__head">
              <th>Item</th>
              <th>Mod</th>
              <th>New version</th>
              <th>Change</th>
              <th>Detected</th>
              <th>Maintenance</th>
            </tr>
          </thead>
          <tbody>
            {updates.map((u, i) => (
              <tr className="table__row" key={`${u.itemId}-${i}`}>
                <td className="mono">{u.itemId}</td>
                <td>{u.modName}</td>
                <td className="mono">{u.newVersion}</td>
                <td>{u.changeClass}</td>
                <td>{fmt(u.detectedAt)}</td>
                <td>
                  {u.acknowledged ? (
                    <span className="count-chip count-chip--ok">Acknowledged</span>
                  ) : onAcknowledge ? (
                    <button type="button" className="btn btn--ghost" disabled={busy} onClick={() => onAcknowledge(u)}>
                      Acknowledge maintenance
                    </button>
                  ) : (
                    <span className="count-chip count-chip--warn">Pending</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ── Workflow execution history ────────────────────────────────────────────────
export function ExecutionHistoryTable({ executions }: { executions: WorkflowExecutionView[] }): JSX.Element {
  if (executions.length === 0) return <p className="muted">No workflow executions recorded yet.</p>;
  return (
    <table className="table" aria-label="Workflow execution history">
      <thead>
        <tr className="table__head">
          <th>Workflow</th>
          <th>Status</th>
          <th>Attempts</th>
          <th>Correlation (event)</th>
          <th>Started</th>
          <th>Completed</th>
          <th>Safe error</th>
        </tr>
      </thead>
      <tbody>
        {executions.map((e) => (
          <tr className="table__row" key={e.id}>
            <td>{e.workflowType}</td>
            <td>{e.status}</td>
            <td>{e.attempts}</td>
            <td className="mono">{e.sourceEventId ?? "—"}</td>
            <td>{fmt(e.startedAt)}</td>
            <td>{fmt(e.completedAt)}</td>
            <td>{e.safeError ?? "—"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function DeadLetterList({
  failures,
  onRetry,
  busyId,
}: {
  failures: WorkflowFailureView[];
  onRetry: (failure: WorkflowFailureView) => void;
  busyId?: string | null;
}): JSX.Element {
  if (failures.length === 0) return <p className="muted">No dead-letter items. Nothing needs manual attention.</p>;
  return (
    <ul className="finding-list" aria-label="Dead-letter items">
      {failures.map((f) => (
        <li className="finding-row" key={f.id}>
          <div className="row" style={{ justifyContent: "space-between", alignItems: "center", width: "100%" }}>
            <div>
              <span className="finding-row__fix-label">
                {f.workflowType} · {f.errorCode}
              </span>
              <span className="finding-row__plain">{f.safeSummary}</span>
            </div>
            <div className="row" style={{ gap: "0.5rem", alignItems: "center" }}>
              <StatusPill tone={failureStateTone(f.state)} label={f.state} />
              <span className="count-chip count-chip--neutral">attempts {f.attempts}</span>
              {f.unsafeCallback ? (
                <span className="count-chip count-chip--danger">Security-rejected — never replayable</span>
              ) : f.replayEligible ? (
                <button type="button" className="btn btn--secondary" disabled={busyId === f.id} onClick={() => onRetry(f)}>
                  Retry
                </button>
              ) : (
                <span className="count-chip count-chip--neutral">Not eligible</span>
              )}
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
}

// ── Inbox ─────────────────────────────────────────────────────────────────────
export function InboxList({
  items,
  onRead,
  onArchive,
  busyId,
}: {
  items: InboxItemView[];
  onRead: (item: InboxItemView) => void;
  onArchive: (item: InboxItemView) => void;
  busyId?: string | null;
}): JSX.Element {
  if (items.length === 0) return <p className="muted">Your inbox is empty.</p>;
  return (
    <ul className="finding-list" aria-label="Notification inbox">
      {items.map((it) => (
        <li className={`finding-row ${it.read ? "list-row--muted" : ""}`} key={it.id} aria-label={`Notification ${it.id}`}>
          <div className="row" style={{ justifyContent: "space-between", alignItems: "center", width: "100%" }}>
            <div>
              <div className="row" style={{ gap: "0.5rem", alignItems: "center" }}>
                <StatusPill tone={inboxTone(it.type)} label={it.type} />
                <strong>{it.title}</strong>
                {it.read ? null : <span className="count-chip count-chip--warn">Unread</span>}
              </div>
              <p className="muted" style={{ margin: "0.25rem 0 0" }}>
                {it.summary}
              </p>
              {it.actionUrl ? (
                <a className="linkish" href={it.actionUrl}>
                  Open action
                </a>
              ) : null}
            </div>
            <div className="row" style={{ gap: "0.5rem" }}>
              {it.read ? null : (
                <button type="button" className="btn btn--ghost" disabled={busyId === it.id} onClick={() => onRead(it)}>
                  Mark read
                </button>
              )}
              <button type="button" className="btn btn--ghost" disabled={busyId === it.id} onClick={() => onArchive(it)}>
                Archive
              </button>
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
}

export function IncidentGroups({ incidents }: { incidents: IncidentGroupView[] }): JSX.Element {
  if (incidents.length === 0) return <p className="muted">No grouped incidents.</p>;
  return (
    <ul className="finding-list" aria-label="Grouped incidents">
      {incidents.map((g) => (
        <li className="finding-row ob-finding--warn" key={g.incidentGroup}>
          <span className="finding-row__fix-label">
            {g.sample.title} · {g.count} occurrence(s)
          </span>
          <span className="finding-row__plain">
            {g.sample.summary} · latest {fmt(g.latestAt)}
          </span>
        </li>
      ))}
    </ul>
  );
}

// ── Guided step list (RSS + n8n flows) ────────────────────────────────────────
export interface FlowStep {
  id: string;
  label: string;
  done: boolean;
}
export function StepRail({ steps, activeId }: { steps: FlowStep[]; activeId: string }): JSX.Element {
  return (
    <ol className="ob-steps" aria-label="Setup steps">
      {steps.map((s) => (
        <li key={s.id} className={`ob-step ${s.id === activeId ? "ob-step--active" : ""} ${s.done ? "ob-step--done" : ""}`}>
          {s.label}
        </li>
      ))}
    </ol>
  );
}
